import CortileLockup from '@/assets/cortile/lockup-terracotta-slate.svg';
import {useLogEvent} from '@/hooks/useLogEvent/useLogEvent';
import {Box, Typography} from '@mui/material';
import React, {useCallback, useEffect} from 'react';
import QRCode from 'react-qr-code';
import {CORTILE, FONT} from './tokens';

const STORES = {
  ios: {
    label: 'App Store',
    event: 'App Store' as const,
    url: 'https://apps.apple.com/gb/app/ark-residents/id1514785878',
  },
  android: {
    label: 'Google Play',
    event: 'Play Store' as const,
    url: 'https://play.google.com/store/apps/details?id=com.projectark.app&hl=en_GB&gl=GB',
  },
};

type Platform = keyof typeof STORES;

const detectPlatform = (): Platform | null => {
  const userAgent = navigator.userAgent;
  if (/android/i.test(userAgent)) return 'android';
  if (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return null;
};

type LinkButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant: 'primary' | 'secondary';
};

const LinkButton = ({variant, children, ...props}: LinkButtonProps) => {
  const primary = variant === 'primary';
  return (
    <Box
      component="a"
      {...props}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: primary ? 48 : 44,
        px: 2.5,
        borderRadius: '8px',
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: primary ? 16 : 14,
        textDecoration: 'none',
        color: primary ? CORTILE.cream : CORTILE.slate,
        bgcolor: primary ? CORTILE.slate : '#FFFFFF',
        border: primary ? 'none' : `1px solid ${CORTILE.border}`,
        '&:hover': {
          bgcolor: primary ? CORTILE.slateHover : '#FFFFFF',
          borderColor: CORTILE.slate,
        },
        '&:focus-visible': {outline: 'none', boxShadow: CORTILE.focusRing},
      }}
    >
      {children}
    </Box>
  );
};

const Heading = ({children}: {children: React.ReactNode}) => (
  <Typography
    component="h1"
    sx={{
      m: 0,
      fontFamily: FONT,
      fontSize: 32,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
      color: CORTILE.slate,
    }}
  >
    {children}
  </Typography>
);

const Body = ({children}: {children: React.ReactNode}) => (
  <Typography
    sx={{
      m: 0,
      fontFamily: FONT,
      fontSize: 15,
      lineHeight: 1.55,
      color: CORTILE.muted,
      textWrap: 'pretty',
    }}
  >
    {children}
  </Typography>
);

const OnPhone = ({platform}: {platform: Platform}) => {
  const store = STORES[platform];
  return (
    <>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}>
        <Heading>Opening the {store.label}…</Heading>
        <Body>
          If nothing happens, tap the button below to download the Cortile app.
        </Body>
      </Box>
      <LinkButton
        variant="primary"
        href={store.url}
        data-attr="cortile-login-store-open"
      >
        Open the {store.label}
      </LinkButton>
    </>
  );
};

const OnDesktop = ({
  onStoreClick,
}: {
  onStoreClick: (platform: Platform) => void;
}) => (
  <>
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}>
      <Heading>Get the Cortile app</Heading>
      <Body>
        The Cortile app is made for your phone. It&rsquo;s where residents get
        building updates, message the team, collect packages and book
        facilities.
      </Body>
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 3,
        p: 2.5,
        bgcolor: '#FFFFFF',
        border: `1px solid ${CORTILE.divider}`,
        borderRadius: '12px',
      }}
    >
      <Box
        role="img"
        aria-label="QR code to download the Cortile app"
        sx={{flexShrink: 0, lineHeight: 0}}
      >
        <QRCode
          value={`${window.location.origin}/download-app`}
          size={112}
          level="M"
          fgColor={CORTILE.slate}
          bgColor="#FFFFFF"
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flex: '1 1 200px',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            m: 0,
            fontFamily: FONT,
            fontSize: 15,
            fontWeight: 600,
            color: CORTILE.slate,
          }}
        >
          Scan with your phone&rsquo;s camera
        </Typography>
        <Body>Or download it directly:</Body>
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
          {(Object.keys(STORES) as Platform[]).map(platform => (
            <LinkButton
              key={platform}
              variant="secondary"
              href={STORES[platform].url}
              target="_blank"
              rel="noopener"
              onClick={() => onStoreClick(platform)}
              data-attr="cortile-login-store-select"
            >
              {STORES[platform].label}
            </LinkButton>
          ))}
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        pt: 3,
        borderTop: `1px solid ${CORTILE.divider}`,
      }}
    >
      <Body>
        <Box component="strong" sx={{color: CORTILE.slate, fontWeight: 600}}>
          Managing a building?
        </Box>{' '}
        The Cortile dashboard runs in your browser, so there&rsquo;s nothing to
        install.
      </Body>
      <LinkButton
        variant="primary"
        href="/login"
        data-attr="cortile-login-dashboard-open"
      >
        Log in to the dashboard
      </LinkButton>
    </Box>
  </>
);

export const CortileDownloadApp = () => {
  const logEvent = useLogEvent();
  const platform = detectPlatform();

  const logStore = useCallback(
    (store: Platform) =>
      logEvent({
        name: 'App Store Redirect',
        data: {store: STORES[store].event},
      }),
    [logEvent],
  );

  useEffect(() => {
    if (!platform) return;
    logStore(platform);
    window.location.replace(STORES[platform].url);
  }, [platform, logStore]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: CORTILE.cream,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: {xs: 'flex-start', md: 'center'},
        px: 3,
        py: {xs: 5, md: 8},
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <Box
          component="img"
          src={CortileLockup}
          alt="Cortile"
          sx={{height: 30, width: 'auto', alignSelf: 'flex-start'}}
        />
        {platform && <OnPhone platform={platform} />}
        {!platform && <OnDesktop onStoreClick={logStore} />}
      </Box>
    </Box>
  );
};
